/**
 * G1 — item registry. One row per item that can exist in a run.
 *
 * Scope per MVP_CHECKLIST's recommended list: the Potion family +
 * status cures that drop from scope-map item balls or heal what the
 * status system can inflict, plus the two Key Items. NO Pokéballs
 * (no catching), NO Repels, NO TMs (data inspection 2026-08-02 found
 * zero TM item balls in scope maps — G8 cut per its own conditional).
 *
 * `effect` is interpreted by the use flow (G6/G7):
 *   heal   — restore N HP to the target party member.
 *   cure   — clear the target's status if it's in `cures`.
 *   key    — no target; sets `FLAG_USED_<ID>` and shows flavor text.
 */
import type { PartyMemberStatus } from '../battle/statusVisuals';

export type ItemEffect =
  | { kind: 'heal'; amount: number }
  | { kind: 'cure'; cures: ReadonlyArray<Exclude<PartyMemberStatus, null>> }
  | { kind: 'key' };

export interface ItemDef {
  id: string;
  /** Display name, cartridge capitalization. */
  name: string;
  /** One-line BagScene description. */
  desc: string;
  effect: ItemEffect;
}

export const ITEMS: Record<string, ItemDef> = {
  potion: {
    id: 'potion',
    name: 'POTION',
    desc: 'Restores 20 HP.',
    effect: { kind: 'heal', amount: 20 },
  },
  super_potion: {
    id: 'super_potion',
    name: 'SUPER POTION',
    desc: 'Restores 50 HP.',
    effect: { kind: 'heal', amount: 50 },
  },
  antidote: {
    id: 'antidote',
    name: 'ANTIDOTE',
    desc: 'Cures poison.',
    effect: { kind: 'cure', cures: ['psn', 'tox'] },
  },
  paralyze_heal: {
    id: 'paralyze_heal',
    name: 'PARALYZ HEAL',
    desc: 'Cures paralysis.',
    effect: { kind: 'cure', cures: ['par'] },
  },
  awakening: {
    id: 'awakening',
    name: 'AWAKENING',
    desc: 'Wakes a sleeping POKEMON.',
    effect: { kind: 'cure', cures: ['slp'] },
  },
  burn_heal: {
    id: 'burn_heal',
    name: 'BURN HEAL',
    desc: 'Cures a burn.',
    effect: { kind: 'cure', cures: ['brn'] },
  },
  ice_heal: {
    id: 'ice_heal',
    name: 'ICE HEAL',
    desc: 'Thaws a frozen POKEMON.',
    effect: { kind: 'cure', cures: ['frz'] },
  },
  town_map: {
    id: 'town_map',
    name: 'TOWN MAP',
    desc: 'A map of the KANTO region.',
    effect: { kind: 'key' },
  },
  old_rod: {
    id: 'old_rod',
    name: 'OLD ROD',
    desc: 'An old fishing rod.',
    effect: { kind: 'key' },
  },
};

/** Item-ball scriptRef → what pickup grants + which FLAG_HIDE_* the
 *  handler SETs to vanish the ball (G3 consumes this). Every ref here
 *  ships in a scope-map runtime.json; hideFlag mirrors that npc
 *  event's `flag` field (static mirror — the handler has no access to
 *  the event data).
 *
 *  Two pragmatic stand-ins (same philosophy as dialogue.ts):
 *  the cartridge's POKE BALL drop is meaningless without catching,
 *  and ETHER is meaningless while PP isn't persisted between
 *  battles — both become the nearest useful item. */
export interface ItemBallDef {
  itemId: string;
  hideFlag: string;
}

export const ITEM_BALLS: Record<string, ItemBallDef> = {
  ViridianCity_EventScript_ItemPotion: {
    itemId: 'potion',
    hideFlag: 'FLAG_HIDE_VIRIDIAN_CITY_POTION',
  },
  ViridianForest_EventScript_ItemPotion: {
    itemId: 'potion',
    hideFlag: 'FLAG_HIDE_VIRIDIAN_FOREST_POTION',
  },
  ViridianForest_EventScript_ItemPotion2: {
    itemId: 'potion',
    hideFlag: 'FLAG_HIDE_VIRIDIAN_FOREST_POTION_2',
  },
  ViridianForest_EventScript_ItemAntidote: {
    itemId: 'antidote',
    hideFlag: 'FLAG_HIDE_VIRIDIAN_FOREST_ANTIDOTE',
  },
  // No catching here — the ball contains a potion.
  ViridianForest_EventScript_ItemPokeBall: {
    itemId: 'potion',
    hideFlag: 'FLAG_HIDE_VIRIDIAN_FOREST_POKE_BALL',
  },
  Route2_EventScript_ItemParalyzeHeal: {
    itemId: 'paralyze_heal',
    hideFlag: 'FLAG_HIDE_ROUTE2_PARALYZE_HEAL',
  },
  // PP not persisted — the ball contains a super potion.
  Route2_EventScript_ItemEther: {
    itemId: 'super_potion',
    hideFlag: 'FLAG_HIDE_ROUTE2_ETHER',
  },
};
